import React from "react";
import { useRestApiProvider } from "@jugl-web/rest-api";
import {
  DataLoadingWrapper,
  EmptyListContent,
  PlainButton,
} from "@jugl-web/ui-components";
import { useTranslations } from "@jugl-web/utils";
import Lottie from "react-lottie";
import DOMPurify from "dompurify";
import { useKnowledgeTraining } from "@web-src/modules/cx/providers/KnowledgeTrainingProvider";
import { ReactComponent as ArrowIcon } from "./assets/arrow.svg";
import { ReactComponent as LinkIcon } from "./assets/link.svg";
import { ReactComponent as TrashIcon } from "./assets/trash.svg";
import { AgentKnowledgeContentHeader } from "../../../AgentKnowledgeContentHeader";
import emptyAnimation from "./assets/empty.json";
import { BlockingOverlayLoader } from "../../../BlockingOverlayLoader/BlockingOverlayLoader";

export const LinkKnowledgePreview: React.FC<{
  entityId: string;
  url: string;
  sourceId: string;
  onDelete: () => void;
  onRequestClose: () => void;
}> = ({ entityId, url, sourceId, onRequestClose, onDelete }) => {
  const { aiWorkflowsApi } = useRestApiProvider();
  const { t } = useTranslations();
  const { trainingStatus } = useKnowledgeTraining();

  const { data, isLoading, isFetching, isError, refetch } =
    aiWorkflowsApi.useGetLinkContentQuery({
      entityId,
      agentId: entityId,
      url,
      source_id: sourceId,
    });

  return (
    <div className="flex h-full w-full flex-col bg-white">
      <AgentKnowledgeContentHeader
        icon={<LinkIcon />}
        title={url}
        startContent={
          <PlainButton
            className="flex h-6 w-10 shrink-0 items-center justify-center"
            onClick={onRequestClose}
            disabled={trainingStatus === "in_process"}
          >
            <ArrowIcon />
          </PlainButton>
        }
        endContent={
          <PlainButton
            className="flex h-6 w-10 shrink-0 items-center justify-center"
            onClick={onDelete}
            disabled={trainingStatus === "in_process"}
          >
            <TrashIcon />
          </PlainButton>
        }
      />
      <div className="relative h-full min-h-0">
        <DataLoadingWrapper
          isLoading={isLoading || isFetching}
          isError={isError}
          onRetry={refetch}
        >
          <BlockingOverlayLoader />
          {(() => {
            if (!data) return null;
            if (data.content) {
              return (
                <div
                  className="jugl__custom-scrollbar h-full overflow-y-auto px-10 py-8"
                  // eslint-disable-next-line react/no-danger
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(data.content, {
                      ADD_ATTR: ["target"],
                      FORBID_ATTR: ["style", "class", "className", "id"],
                      FORBID_TAGS: ["a"],
                    }),
                  }}
                />
              );
            }
            return (
              <EmptyListContent
                type="noResults"
                customImg={
                  <Lottie
                    options={{ animationData: emptyAnimation }}
                    width={200}
                    height={200}
                  />
                }
                customSubtitle={t({
                  id: "cx-link-knowledge-preview.link-not-contain-data",
                  defaultMessage: "Link does not contain any data",
                })}
              />
            );
          })()}
        </DataLoadingWrapper>
      </div>
    </div>
  );
};
